import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  calcularIPER,
  interpretarProbabilidad,
  interpretarRiesgo,
  NIVELES_DEFICIENCIA,
  NIVELES_EXPOSICION,
  NIVELES_CONSECUENCIA,
  CATEGORIAS_PELIGRO,
  RIESGO_COLORS,
} from '../lib/iperCalculations';
import type { NivelDeficiencia, NivelExposicion, NivelConsecuencia } from '../lib/iperCalculations';
import { hoyEc } from '../lib/fechas';

// ── Types ──────────────────────────────────────────────────────────────────────

interface IperAssessment {
  id:                           string;
  company_id:                   string;
  warehouse_id:                 string | null;
  position_id:                  string | null;
  process_name:                 string;
  zone_location:                string;
  activity:                     string;
  task:                         string;
  is_routine:                   boolean;
  hazard_category:              string;
  hazard_type:                  string;
  hazard_description:           string | null;
  possible_effects:             string | null;
  control_source:               string | null;
  control_medium:               string | null;
  control_individual:           string | null;
  nivel_deficiencia:            number;
  nivel_exposicion:             number;
  nivel_probabilidad:           number;
  interpretacion_probabilidad:  string;
  nivel_consecuencia:           number;
  nivel_riesgo:                 number;
  interpretacion_riesgo:        string;
  aceptabilidad:                string;
  nivel_deficiencia_residual:           number | null;
  nivel_exposicion_residual:            number | null;
  nivel_probabilidad_residual:          number | null;
  interpretacion_probabilidad_residual: string | null;
  nivel_consecuencia_residual:          number | null;
  nivel_riesgo_residual:                number | null;
  interpretacion_riesgo_residual:       string | null;
  aceptabilidad_residual:               string | null;
  num_expuestos:                number | null;
  peor_consecuencia:            string | null;
  requisito_legal:              boolean;
  medida_eliminacion:           string | null;
  medida_sustitucion:           string | null;
  medida_control_ingenieria:    string | null;
  medida_control_administrativo: string | null;
  medida_epp:                   string | null;
  evaluado_por:                 string | null;
  fecha_evaluacion:             string;
  created_by:                   string | null;
  created_at:                   string;
  updated_at:                   string;
}

interface IperHazardCatalogItem {
  id:            string;
  category:      string;
  hazard_type:   string;
  display_order: number;
}

interface WarehouseOption { id: string; name: string; }

interface SuggestedHazard { id: string; category: string; hazard_type: string; }

interface EppCatalogItem { id: string; name: string; category: string | null; }

// ── Helpers ────────────────────────────────────────────────────────────────────

function makeEmptyForm() {
  return {
    process_name:                 '',
    zone_location:                '',
    warehouse_id:                 '',
    position_id:                  '',
    activity:                     '',
    task:                         '',
    is_routine:                   true,
    hazard_category:              '',
    hazard_type:                  '',
    hazard_type_other:            '',
    hazard_description:           '',
    possible_effects:             '',
    control_source:               '',
    control_medium:               '',
    control_individual:           '',
    nivel_deficiencia:            '',
    nivel_exposicion:             '',
    nivel_consecuencia:           '',
    nivel_deficiencia_residual:   '',
    nivel_exposicion_residual:    '',
    nivel_consecuencia_residual:  '',
    num_expuestos:                '',
    peor_consecuencia:            '',
    requisito_legal:              false,
    medida_eliminacion:           '',
    medida_sustitucion:           '',
    medida_control_ingenieria:    '',
    medida_control_administrativo: '',
    medida_epp:                   '',
    evaluado_por:                 '',
    fecha_evaluacion:             hoyEc(),
  };
}

function getNivelCategoria(a: IperAssessment): string {
  return interpretarRiesgo(a.nivel_riesgo, a.nivel_deficiencia as NivelDeficiencia).nivel;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function IperPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';

  // ── State ─────────────────────────────────────────────────────────────────

  const [assessments,   setAssessments]   = useState<IperAssessment[]>([]);
  const [warehouses,    setWarehouses]    = useState<WarehouseOption[]>([]);
  const [jobPositions,  setJobPositions]  = useState<{ id: string; name: string }[]>([]);
  const [catalogItems,  setCatalogItems]  = useState<IperHazardCatalogItem[]>([]);
  const [eppCatalog,    setEppCatalog]    = useState<EppCatalogItem[]>([]);
  const [loading,       setLoading]       = useState(true);

  const [filterCategory, setFilterCategory] = useState('');
  const [filterNivel,    setFilterNivel]    = useState('');
  const [filterSearch,   setFilterSearch]   = useState('');

  const [showModal,   setShowModal]   = useState(false);
  const [editTarget,  setEditTarget]  = useState<IperAssessment | null>(null);
  const [form,        setForm]        = useState(makeEmptyForm);
  const [saving,      setSaving]      = useState(false);

  const [suggestedHazards, setSuggestedHazards] = useState<SuggestedHazard[]>([]);

  const [deleteTarget, setDeleteTarget] = useState<IperAssessment | null>(null);
  const [deleteStep,   setDeleteStep]   = useState(1);
  const [deleting,     setDeleting]     = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadData = useCallback(async (silent = false) => {
    if (!companyId) return;
    if (!silent) setLoading(true);
    const [{ data: asmt }, { data: wh }, { data: jps }] = await Promise.all([
      supabase.from('iper_assessments').select('*').eq('company_id', companyId).order('fecha_evaluacion', { ascending: false }),
      supabase.from('warehouses').select('id, name').eq('company_id', companyId).order('name'),
      supabase.from('job_positions').select('id, name').eq('company_id', companyId).order('name'),
    ]);
    setAssessments((asmt ?? []) as IperAssessment[]);
    setWarehouses((wh ?? []) as WarehouseOption[]);
    setJobPositions((jps ?? []) as { id: string; name: string }[]);
    if (!silent) setLoading(false);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    supabase.from('iper_hazard_catalog').select('*').order('display_order')
      .then(({ data }) => setCatalogItems((data ?? []) as IperHazardCatalogItem[]));
  }, []);

  useEffect(() => {
    if (!companyId) return;
    supabase.from('epp_catalog').select('id, name, category').eq('company_id', companyId).order('name')
      .then(({ data }) => setEppCatalog((data ?? []) as EppCatalogItem[]));
  }, [companyId]);

  useEffect(() => {
    if (!form.position_id) { setSuggestedHazards([]); return; }
    let cancelled = false;
    supabase
      .from('job_position_typical_hazards')
      .select('hazard_catalog_id')
      .eq('job_position_id', form.position_id)
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data ?? []) as { hazard_catalog_id: string }[];
        const suggestions = rows
          .map(r => catalogItems.find(c => c.id === r.hazard_catalog_id))
          .filter((c): c is IperHazardCatalogItem => !!c)
          .map(c => ({ id: c.id, category: c.category, hazard_type: c.hazard_type }));
        setSuggestedHazards(suggestions);
      });
    return () => { cancelled = true; };
  }, [form.position_id, catalogItems]);

  // ── Permissions ──────────────────────────────────────────────────────────────

  const canEdit   = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  // ── Live calculation ──────────────────────────────────────────────────────────

  const nd = parseInt(form.nivel_deficiencia, 10);
  const ne = parseInt(form.nivel_exposicion,  10);
  const nc = parseInt(form.nivel_consecuencia, 10);

  const liveCalc = (!isNaN(nd) && !isNaN(ne) && !isNaN(nc))
    ? calcularIPER(nd as NivelDeficiencia, ne as NivelExposicion, nc as NivelConsecuencia)
    : null;

  const ndR = parseInt(form.nivel_deficiencia_residual, 10);
  const neR = parseInt(form.nivel_exposicion_residual,  10);
  const ncR = parseInt(form.nivel_consecuencia_residual, 10);

  const liveCalcResidual = (!isNaN(ndR) && !isNaN(neR) && !isNaN(ncR))
    ? calcularIPER(ndR as NivelDeficiencia, neR as NivelExposicion, ncR as NivelConsecuencia)
    : null;

  // ── Derived / filtered ────────────────────────────────────────────────────────

  const totalCount = assessments.length;
  const countI     = assessments.filter(a => getNivelCategoria(a) === 'I').length;
  const countII    = assessments.filter(a => getNivelCategoria(a) === 'II').length;
  const countIII   = assessments.filter(a => getNivelCategoria(a) === 'III').length;
  const countIV    = assessments.filter(a => getNivelCategoria(a) === 'IV').length;

  const filteredAssessments = assessments.filter(a => {
    if (filterCategory && a.hazard_category !== filterCategory) return false;
    if (filterNivel    && getNivelCategoria(a) !== filterNivel)  return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (!a.process_name.toLowerCase().includes(q) && !a.activity.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const filteredHazardTypes = form.hazard_category
    ? catalogItems.filter(c => c.category === form.hazard_category && c.hazard_type !== 'Otros')
    : [];

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function openCreate() {
    setEditTarget(null);
    setForm(makeEmptyForm());
    setShowModal(true);
  }

  function openEdit(a: IperAssessment) {
    setEditTarget(a);
    const knownHazardType = catalogItems.some(c => c.category === a.hazard_category && c.hazard_type === a.hazard_type);
    setForm({
      process_name:                 a.process_name,
      zone_location:                a.zone_location,
      warehouse_id:                 a.warehouse_id ?? '',
      position_id:                  a.position_id ?? '',
      activity:                     a.activity,
      task:                         a.task,
      is_routine:                   a.is_routine,
      hazard_category:              a.hazard_category,
      hazard_type:                  knownHazardType ? a.hazard_type : '__otro__',
      hazard_type_other:            knownHazardType ? '' : a.hazard_type,
      hazard_description:           a.hazard_description ?? '',
      possible_effects:             a.possible_effects ?? '',
      control_source:               a.control_source ?? '',
      control_medium:               a.control_medium ?? '',
      control_individual:           a.control_individual ?? '',
      nivel_deficiencia:            String(a.nivel_deficiencia),
      nivel_exposicion:             String(a.nivel_exposicion),
      nivel_consecuencia:           String(a.nivel_consecuencia),
      nivel_deficiencia_residual:   a.nivel_deficiencia_residual  != null ? String(a.nivel_deficiencia_residual)  : '',
      nivel_exposicion_residual:    a.nivel_exposicion_residual   != null ? String(a.nivel_exposicion_residual)   : '',
      nivel_consecuencia_residual:  a.nivel_consecuencia_residual != null ? String(a.nivel_consecuencia_residual) : '',
      num_expuestos:                a.num_expuestos != null ? String(a.num_expuestos) : '',
      peor_consecuencia:            a.peor_consecuencia ?? '',
      requisito_legal:              a.requisito_legal,
      medida_eliminacion:           a.medida_eliminacion ?? '',
      medida_sustitucion:           a.medida_sustitucion ?? '',
      medida_control_ingenieria:    a.medida_control_ingenieria ?? '',
      medida_control_administrativo: a.medida_control_administrativo ?? '',
      medida_epp:                   a.medida_epp ?? '',
      evaluado_por:                 a.evaluado_por ?? '',
      fecha_evaluacion:             a.fecha_evaluacion,
    });
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.process_name.trim())   { alert('El proceso es requerido.');                  return; }
    if (!form.zone_location.trim())  { alert('La zona/lugar es requerido.');               return; }
    if (!form.activity.trim())       { alert('La actividad es requerida.');                return; }
    if (!form.hazard_category)       { alert('La categoría de peligro es requerida.');     return; }
    if (!form.hazard_type)           { alert('El tipo de peligro es requerido.');          return; }
    if (form.hazard_type === '__otro__' && !form.hazard_type_other.trim()) {
      alert('Especifica el tipo de peligro.');
      return;
    }
    if (!form.nivel_deficiencia)     { alert('El nivel de deficiencia es requerido.');     return; }
    if (!form.nivel_exposicion)      { alert('El nivel de exposición es requerido.');      return; }
    if (!form.nivel_consecuencia)    { alert('El nivel de consecuencia es requerido.');    return; }
    if (!form.evaluado_por.trim())   { alert('El campo "Evaluado por" es requerido.');     return; }
    if (!form.fecha_evaluacion)      { alert('La fecha de evaluación es requerida.');      return; }
    if (!liveCalc) { alert('Completa los niveles de deficiencia, exposición y consecuencia.'); return; }

    setSaving(true);

    const finalHazardType = form.hazard_type === '__otro__' ? form.hazard_type_other.trim() : form.hazard_type;

    const basePayload = {
      company_id:                   companyId,
      warehouse_id:                 form.warehouse_id || null,
      position_id:                  form.position_id || null,
      process_name:                 form.process_name.trim(),
      zone_location:                form.zone_location.trim(),
      activity:                     form.activity.trim(),
      task:                         form.task.trim(),
      is_routine:                   form.is_routine,
      hazard_category:              form.hazard_category,
      hazard_type:                  finalHazardType,
      hazard_description:           form.hazard_description.trim() || null,
      possible_effects:             form.possible_effects.trim() || null,
      control_source:               form.control_source.trim() || null,
      control_medium:               form.control_medium.trim() || null,
      control_individual:           form.control_individual.trim() || null,
      nivel_deficiencia:            nd,
      nivel_exposicion:             ne,
      nivel_probabilidad:           liveCalc.nivel_probabilidad,
      interpretacion_probabilidad:  liveCalc.interpretacion_probabilidad,
      nivel_consecuencia:           nc,
      nivel_riesgo:                 liveCalc.nivel_riesgo,
      interpretacion_riesgo:        liveCalc.interpretacion_riesgo,
      aceptabilidad:                liveCalc.aceptabilidad,
      num_expuestos:                form.num_expuestos ? parseInt(form.num_expuestos, 10) : null,
      peor_consecuencia:            form.peor_consecuencia.trim() || null,
      requisito_legal:              form.requisito_legal,
      medida_eliminacion:           form.medida_eliminacion.trim() || null,
      medida_sustitucion:           form.medida_sustitucion.trim() || null,
      medida_control_ingenieria:    form.medida_control_ingenieria.trim() || null,
      medida_control_administrativo: form.medida_control_administrativo.trim() || null,
      medida_epp:                   form.medida_epp.trim() || null,
      evaluado_por:                 form.evaluado_por.trim() || null,
      fecha_evaluacion:             form.fecha_evaluacion,
      nivel_deficiencia_residual:           liveCalcResidual ? ndR : null,
      nivel_exposicion_residual:            liveCalcResidual ? neR : null,
      nivel_probabilidad_residual:          liveCalcResidual ? liveCalcResidual.nivel_probabilidad : null,
      interpretacion_probabilidad_residual: liveCalcResidual ? liveCalcResidual.interpretacion_probabilidad : null,
      nivel_consecuencia_residual:          liveCalcResidual ? ncR : null,
      nivel_riesgo_residual:                liveCalcResidual ? liveCalcResidual.nivel_riesgo : null,
      interpretacion_riesgo_residual:       liveCalcResidual ? liveCalcResidual.interpretacion_riesgo : null,
      aceptabilidad_residual:               liveCalcResidual ? liveCalcResidual.aceptabilidad : null,
      updated_at:                   new Date().toISOString(),
    };

    if (editTarget) {
      const { error } = await supabase.from('iper_assessments').update(basePayload).eq('id', editTarget.id);
      if (error) { alert('Error al actualizar: ' + error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('iper_assessments').insert({ ...basePayload, created_by: profile?.id ?? null });
      if (error) { alert('Error al crear: ' + error.message); setSaving(false); return; }
    }

    setSaving(false);
    setShowModal(false);
    setEditTarget(null);
    await loadData(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await supabase.from('iper_assessments').delete().eq('id', deleteTarget.id);
    if (error) { alert('Error al eliminar: ' + error.message); setDeleting(false); return; }
    setDeleting(false);
    setDeleteTarget(null);
    setDeleteStep(1);
    loadData(true);
  }

  // ── Plan gate (after all hooks) ──────────────────────────────────────────────

  const isSuperAdmin = profile?.role === 'superadmin';

  if (!isSuperAdmin && displayCompany && !hasPlanAccess(displayCompany.plan, 'iper')) {
    return <UpgradeWall {...PLAN_FEATURES.iper} />;
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Matriz de Riesgos</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            Identificación de Peligros y Evaluación de Riesgos — GTC 45
          </p>
        </div>
        {canEdit && (
          <button onClick={openCreate} style={primaryBtn}>+ Nueva evaluación</button>
        )}
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <MetricCard label="Total evaluaciones" value={totalCount} color="#64748b" />
        <MetricCard label="Nivel I — Crítico"  value={countI}     color={RIESGO_COLORS['I']} />
        <MetricCard label="Nivel II"            value={countII}    color={RIESGO_COLORS['II']} />
        <MetricCard label="Nivel III"           value={countIII}   color={RIESGO_COLORS['III']} />
        <MetricCard label="Nivel IV — Aceptable" value={countIV}  color={RIESGO_COLORS['IV']} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ ...sel, maxWidth: 220 }}>
          <option value="">Todas las categorías</option>
          {CATEGORIAS_PELIGRO.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}
          style={{ ...sel, maxWidth: 160 }}>
          <option value="">Todos los niveles</option>
          {['I', 'II', 'III', 'IV'].map(n => <option key={n} value={n}>Nivel {n}</option>)}
        </select>
        <input
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          placeholder="Buscar por proceso o actividad…"
          style={{ ...inp, maxWidth: 280, flex: 1 }}
        />
        {(filterCategory || filterNivel || filterSearch) && (
          <button
            onClick={() => { setFilterCategory(''); setFilterNivel(''); setFilterSearch(''); }}
            style={ghostBtn}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filteredAssessments.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {assessments.length === 0 ? 'Aún no hay evaluaciones registradas.' : 'Ninguna evaluación coincide con los filtros activos.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={thStyle}>Proceso / Zona</th>
                  <th style={thStyle}>Actividad / Tarea</th>
                  <th style={thStyle}>Categoría de peligro</th>
                  <th style={thStyle}>Nivel de riesgo</th>
                  <th style={thStyle}>Aceptabilidad</th>
                  <th style={thStyle}>Fecha eval.</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.map(a => {
                  const nivel      = getNivelCategoria(a);
                  const nivelColor = RIESGO_COLORS[nivel];
                  const catLabel   = CATEGORIAS_PELIGRO.find(c => c.value === a.hazard_category)?.label ?? a.hazard_category;
                  return (
                    <tr key={a.id}
                      style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                      <td style={td}>
                        <span style={{ color: '#e2e8f0', fontSize: '0.84rem', display: 'block' }}>{a.process_name}</span>
                        <span style={{ color: '#64748b', fontSize: '0.74rem' }}>{a.zone_location}</span>
                      </td>
                      <td style={td}>
                        <span style={{ color: '#cbd5e1', fontSize: '0.84rem', display: 'block' }}>{a.activity}</span>
                        <span style={{ color: '#64748b', fontSize: '0.74rem' }}>{a.task}</span>
                      </td>
                      <td style={td}>
                        <span style={{ background: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: '1px solid rgba(100,116,139,0.25)', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, display: 'inline-block', marginBottom: 3 }}>
                          {catLabel}
                        </span>
                        <br />
                        <span style={{ color: '#64748b', fontSize: '0.74rem' }}>{a.hazard_type}</span>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <span style={{ background: `${nivelColor}20`, color: nivelColor, border: `1px solid ${nivelColor}50`, borderRadius: 6, padding: '4px 12px', fontSize: '0.86rem', fontWeight: 800, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                            Nivel {nivel}
                          </span>
                          <span style={{ color: '#64748b', fontSize: '0.72rem' }}>NR = {a.nivel_riesgo}</span>
                        </div>
                      </td>
                      <td style={td}>
                        <span style={{
                          color: a.aceptabilidad === 'Aceptable' ? RIESGO_COLORS['IV'] : a.aceptabilidad === 'Mejorable' ? RIESGO_COLORS['III'] : RIESGO_COLORS['I'],
                          fontSize: '0.8rem', fontWeight: 600
                        }}>
                          {a.aceptabilidad}
                        </span>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748b', fontSize: '0.8rem' }}>
                        {new Date(a.fecha_evaluacion + 'T00:00:00').toLocaleDateString('es-EC')}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => openEdit(a)} style={ghostBtn}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.color = '#60a5fa'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                            {canEdit ? 'Ver/Editar' : 'Ver'}
                          </button>
                          {canDelete && (
                            <button onClick={() => { setDeleteTarget(a); setDeleteStep(1); }} style={ghostBtn}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}>
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {assessments.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filteredAssessments.length} de {assessments.length} evaluacion{assessments.length !== 1 ? 'es' : ''}
        </p>
      )}

      {/* ── Modal crear / editar ─────────────────────────────────────────────── */}
      {showModal && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <div style={{ ...modalBox, maxWidth: 760 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                {editTarget ? 'Editar evaluación' : 'Nueva evaluación — GTC 45'}
              </h2>
              <button onClick={() => !saving && setShowModal(false)} style={closeBtn}>✕</button>
            </div>

            <div style={{ padding: '0 24px 24px', overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>

              {/* Sección 1 */}
              <SectionDivider title="1. Contexto de la evaluación" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Proceso *">
                    <input style={inp} value={form.process_name}
                      onChange={e => setForm(f => ({ ...f, process_name: e.target.value }))}
                      placeholder="Ej: Recepción de materiales" />
                  </Field>
                  <Field label="Zona / Lugar *">
                    <input style={inp} value={form.zone_location}
                      onChange={e => setForm(f => ({ ...f, zone_location: e.target.value }))}
                      placeholder="Ej: Bodega principal" />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Actividad *">
                    <input style={inp} value={form.activity}
                      onChange={e => setForm(f => ({ ...f, activity: e.target.value }))}
                      placeholder="Ej: Descarga de productos" />
                  </Field>
                  <Field label="Tarea">
                    <input style={inp} value={form.task}
                      onChange={e => setForm(f => ({ ...f, task: e.target.value }))}
                      placeholder="Ej: Manipulación de tambores" />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Bodega (opcional)">
                    <SelectBuscable inputStyle={{ ...inp, paddingRight: 32 }} ariaLabel="Bodega (opcional)" value={form.warehouse_id}
                      onChange={v => setForm(f => ({ ...f, warehouse_id: v }))} vacio="— Sin bodega específica —"
                      opciones={warehouses.map(w => ({ value: w.id, label: w.name }))} />
                  </Field>
                  <Field label="¿Es rutinaria?">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 38 }}>
                      <Toggle checked={form.is_routine} onChange={v => setForm(f => ({ ...f, is_routine: v }))} />
                      <span style={{ color: '#94a3b8', fontSize: '0.84rem' }}>{form.is_routine ? 'Sí' : 'No'}</span>
                    </div>
                  </Field>
                </div>
                <Field label="Puesto de trabajo afectado (opcional)">
                  <SelectBuscable
                    inputStyle={{ ...inp, paddingRight: 32, opacity: jobPositions.length === 0 ? 0.5 : 1 }}
                    ariaLabel="Puesto de trabajo afectado (opcional)"
                    value={form.position_id}
                    onChange={v => setForm(f => ({ ...f, position_id: v }))}
                    disabled={jobPositions.length === 0}
                    vacio="— Sin puesto específico —"
                    opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))}
                  />
                  {jobPositions.length === 0 && (
                    <p style={{ margin: '4px 0 0', fontSize: '0.73rem', color: '#f59e0b' }}>
                      No tienes puestos creados. Ve a <strong>Puestos de Trabajo</strong> para crear uno.
                    </p>
                  )}
                </Field>
              </div>

              {/* Sección 2 */}
              <SectionDivider title="2. Identificación del peligro" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                {suggestedHazards.length > 0 && (
                  <div style={suggestedBoxStyle}>
                    <p style={suggestedLabelStyle}>💡 Riesgos típicos sugeridos para este puesto</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {suggestedHazards.map(s => {
                        const catLabel = CATEGORIAS_PELIGRO.find(c => c.value === s.category)?.label ?? s.category;
                        const isActive = form.hazard_category === s.category && form.hazard_type === s.hazard_type;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            style={{ ...suggestedChipStyle, ...(isActive ? suggestedChipActiveStyle : {}) }}
                            onClick={() => setForm(f => ({ ...f, hazard_category: s.category, hazard_type: s.hazard_type, hazard_type_other: '' }))}
                          >
                            {catLabel} · {s.hazard_type}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Categoría de peligro *">
                    <select style={sel} value={form.hazard_category}
                      onChange={e => setForm(f => ({ ...f, hazard_category: e.target.value, hazard_type: '' }))}>
                      <option value="">— Seleccionar —</option>
                      {CATEGORIAS_PELIGRO.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Tipo de peligro *">
                    <select style={{ ...sel, opacity: !form.hazard_category ? 0.5 : 1 }}
                      value={form.hazard_type}
                      onChange={e => setForm(f => ({ ...f, hazard_type: e.target.value }))}
                      disabled={!form.hazard_category}>
                      <option value="">— Seleccionar categoría primero —</option>
                      {filteredHazardTypes.map(h => <option key={h.id} value={h.hazard_type}>{h.hazard_type}</option>)}
                      {form.hazard_category && <option value="__otro__">Otros</option>}
                    </select>
                  </Field>
                </div>
                {form.hazard_type === '__otro__' && (
                  <Field label="Especifica el tipo de peligro">
                    <input style={inp} value={form.hazard_type_other}
                      onChange={e => setForm(f => ({ ...f, hazard_type_other: e.target.value }))}
                      placeholder="Escribe el tipo de peligro" />
                  </Field>
                )}
                <Field label="Descripción del peligro">
                  <textarea style={ta} rows={2} value={form.hazard_description}
                    onChange={e => setForm(f => ({ ...f, hazard_description: e.target.value }))}
                    placeholder="Describe el peligro identificado…" />
                </Field>
                <Field label="Efectos posibles">
                  <textarea style={ta} rows={2} value={form.possible_effects}
                    onChange={e => setForm(f => ({ ...f, possible_effects: e.target.value }))}
                    placeholder="¿Qué lesiones o enfermedades puede causar?…" />
                </Field>
              </div>

              {/* Sección 3 */}
              <SectionDivider title="3. Controles existentes" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                <Field label="Control en la fuente">
                  <input style={inp} value={form.control_source}
                    onChange={e => setForm(f => ({ ...f, control_source: e.target.value }))}
                    placeholder="Controles aplicados en la fuente del peligro" />
                </Field>
                <Field label="Control en el medio">
                  <input style={inp} value={form.control_medium}
                    onChange={e => setForm(f => ({ ...f, control_medium: e.target.value }))}
                    placeholder="Controles aplicados en el medio de transmisión" />
                </Field>
                <Field label="Control en el individuo">
                  <input style={inp} value={form.control_individual}
                    onChange={e => setForm(f => ({ ...f, control_individual: e.target.value }))}
                    placeholder="Controles aplicados en el individuo (EPP, etc.)" />
                </Field>
              </div>

              {/* Sección 4 — corazón del módulo */}
              <SectionDivider title="4. Evaluación del riesgo — GTC 45" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Field label="Nivel de Deficiencia (ND) *">
                    <select style={sel} value={form.nivel_deficiencia}
                      onChange={e => setForm(f => ({ ...f, nivel_deficiencia: e.target.value }))}>
                      <option value="">— Seleccionar —</option>
                      {NIVELES_DEFICIENCIA.map(n => (
                        <option key={n.value} value={String(n.value)}>{n.label}</option>
                      ))}
                    </select>
                    <HelpText text={NIVELES_DEFICIENCIA.find(n => String(n.value) === form.nivel_deficiencia)?.desc} />
                  </Field>
                  <Field label="Nivel de Exposición (NE) *">
                    <select style={sel} value={form.nivel_exposicion}
                      onChange={e => setForm(f => ({ ...f, nivel_exposicion: e.target.value }))}>
                      <option value="">— Seleccionar —</option>
                      {NIVELES_EXPOSICION.map(n => (
                        <option key={n.value} value={String(n.value)}>{n.label}</option>
                      ))}
                    </select>
                    <HelpText text={NIVELES_EXPOSICION.find(n => String(n.value) === form.nivel_exposicion)?.desc} />
                  </Field>
                  <Field label="Nivel de Consecuencia (NC) *">
                    <select style={sel} value={form.nivel_consecuencia}
                      onChange={e => setForm(f => ({ ...f, nivel_consecuencia: e.target.value }))}>
                      <option value="">— Seleccionar —</option>
                      {NIVELES_CONSECUENCIA.map(n => (
                        <option key={n.value} value={String(n.value)}>{n.label}</option>
                      ))}
                    </select>
                    <HelpText text={NIVELES_CONSECUENCIA.find(n => String(n.value) === form.nivel_consecuencia)?.desc} />
                  </Field>
                </div>

                {/* Calculadora en vivo */}
                {(form.nivel_deficiencia || form.nivel_exposicion || form.nivel_consecuencia) && (
                  <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ color: '#475569', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>
                      Cálculo en tiempo real
                    </p>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      {!isNaN(nd) && !isNaN(ne) && (
                        <div style={{ minWidth: 110 }}>
                          <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block' }}>NP = ND × NE</span>
                          <span style={{ color: '#e2e8f0', fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2 }}>{nd * ne}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.76rem', display: 'block', marginTop: 2 }}>
                            {interpretarProbabilidad(nd * ne)}
                          </span>
                        </div>
                      )}
                      {liveCalc && (
                        <>
                          <div style={{ minWidth: 110 }}>
                            <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block' }}>NR = NP × NC</span>
                            <span style={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2, color: RIESGO_COLORS[liveCalc.nivel_riesgo_categoria] }}>
                              {liveCalc.nivel_riesgo}
                            </span>
                            <span style={{
                              background: `${RIESGO_COLORS[liveCalc.nivel_riesgo_categoria]}20`,
                              color: RIESGO_COLORS[liveCalc.nivel_riesgo_categoria],
                              border: `1px solid ${RIESGO_COLORS[liveCalc.nivel_riesgo_categoria]}50`,
                              borderRadius: 4, padding: '1px 8px', fontSize: '0.78rem', fontWeight: 800, display: 'inline-block', marginTop: 4
                            }}>
                              Nivel {liveCalc.nivel_riesgo_categoria}
                            </span>
                          </div>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>Interpretación</span>
                            <span style={{ color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.5 }}>
                              {liveCalc.interpretacion_riesgo}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>Aceptabilidad</span>
                            <span style={{
                              color: liveCalc.aceptabilidad === 'Aceptable' ? RIESGO_COLORS['IV']
                                   : liveCalc.aceptabilidad === 'Mejorable' ? RIESGO_COLORS['III']
                                   : RIESGO_COLORS['I'],
                              fontSize: '0.88rem', fontWeight: 700
                            }}>
                              {liveCalc.aceptabilidad}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sección 5 */}
              <SectionDivider title="5. Criterios para establecer controles" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Número de expuestos">
                    <input style={inp} type="number" min="0" value={form.num_expuestos}
                      onChange={e => setForm(f => ({ ...f, num_expuestos: e.target.value }))}
                      placeholder="0" />
                  </Field>
                  <Field label="¿Existe requisito legal asociado?">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 38 }}>
                      <Toggle checked={form.requisito_legal} onChange={v => setForm(f => ({ ...f, requisito_legal: v }))} />
                      <span style={{ color: '#94a3b8', fontSize: '0.84rem' }}>{form.requisito_legal ? 'Sí' : 'No'}</span>
                    </div>
                  </Field>
                </div>
                <Field label="Peor consecuencia posible">
                  <input style={inp} value={form.peor_consecuencia}
                    onChange={e => setForm(f => ({ ...f, peor_consecuencia: e.target.value }))}
                    placeholder="Describe la peor consecuencia posible del riesgo" />
                </Field>
              </div>

              {/* Sección 6 */}
              <SectionDivider title="6. Plan de acción — Jerarquía de controles" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                <Field label="Eliminación">
                  <textarea style={ta} rows={2} value={form.medida_eliminacion}
                    onChange={e => setForm(f => ({ ...f, medida_eliminacion: e.target.value }))}
                    placeholder="Medidas para eliminar el peligro en la fuente…" />
                </Field>
                <Field label="Sustitución">
                  <textarea style={ta} rows={2} value={form.medida_sustitucion}
                    onChange={e => setForm(f => ({ ...f, medida_sustitucion: e.target.value }))}
                    placeholder="Medidas para sustituir el peligro por uno de menor riesgo…" />
                </Field>
                <Field label="Controles de ingeniería">
                  <textarea style={ta} rows={2} value={form.medida_control_ingenieria}
                    onChange={e => setForm(f => ({ ...f, medida_control_ingenieria: e.target.value }))}
                    placeholder="Barreras, aislamientos, sistemas de extracción…" />
                </Field>
                <Field label="Controles administrativos / Señalización">
                  <textarea style={ta} rows={2} value={form.medida_control_administrativo}
                    onChange={e => setForm(f => ({ ...f, medida_control_administrativo: e.target.value }))}
                    placeholder="Procedimientos, capacitación, rotación de puestos…" />
                </Field>
                <Field label="Equipos de protección personal (EPP)">
                  <textarea style={ta} rows={2} value={form.medida_epp}
                    onChange={e => setForm(f => ({ ...f, medida_epp: e.target.value }))}
                    placeholder="Tipo de EPP requerido…" />
                  {eppCatalog.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {eppCatalog.map(epp => (
                        <button
                          key={epp.id}
                          type="button"
                          style={suggestedChipStyle}
                          onClick={() => setForm(f => {
                            if (f.medida_epp.includes(epp.name)) return f;
                            return {
                              ...f,
                              medida_epp: f.medida_epp.trim() ? `${f.medida_epp}, ${epp.name}` : epp.name,
                            };
                          })}
                        >
                          {epp.name}
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
              </div>

              {/* Sección 7 — Riesgo residual (opcional) */}
              <SectionDivider title="7. Riesgo residual (después de controles)" />
              <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '4px 0 14px' }}>
                Opcional — calcula el riesgo después de implementar las medidas de control documentadas arriba.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Field label="Nivel de Deficiencia residual (ND)">
                    <select style={sel} value={form.nivel_deficiencia_residual}
                      onChange={e => setForm(f => ({ ...f, nivel_deficiencia_residual: e.target.value }))}>
                      <option value="">— Seleccionar —</option>
                      {NIVELES_DEFICIENCIA.map(n => (
                        <option key={n.value} value={String(n.value)}>{n.label}</option>
                      ))}
                    </select>
                    <HelpText text={NIVELES_DEFICIENCIA.find(n => String(n.value) === form.nivel_deficiencia_residual)?.desc} />
                  </Field>
                  <Field label="Nivel de Exposición residual (NE)">
                    <select style={sel} value={form.nivel_exposicion_residual}
                      onChange={e => setForm(f => ({ ...f, nivel_exposicion_residual: e.target.value }))}>
                      <option value="">— Seleccionar —</option>
                      {NIVELES_EXPOSICION.map(n => (
                        <option key={n.value} value={String(n.value)}>{n.label}</option>
                      ))}
                    </select>
                    <HelpText text={NIVELES_EXPOSICION.find(n => String(n.value) === form.nivel_exposicion_residual)?.desc} />
                  </Field>
                  <Field label="Nivel de Consecuencia residual (NC)">
                    <select style={sel} value={form.nivel_consecuencia_residual}
                      onChange={e => setForm(f => ({ ...f, nivel_consecuencia_residual: e.target.value }))}>
                      <option value="">— Seleccionar —</option>
                      {NIVELES_CONSECUENCIA.map(n => (
                        <option key={n.value} value={String(n.value)}>{n.label}</option>
                      ))}
                    </select>
                    <HelpText text={NIVELES_CONSECUENCIA.find(n => String(n.value) === form.nivel_consecuencia_residual)?.desc} />
                  </Field>
                </div>

                {(form.nivel_deficiencia_residual || form.nivel_exposicion_residual || form.nivel_consecuencia_residual) && (
                  <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ color: '#475569', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>
                      Cálculo en tiempo real — riesgo residual
                    </p>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      {!isNaN(ndR) && !isNaN(neR) && (
                        <div style={{ minWidth: 110 }}>
                          <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block' }}>NP = ND × NE</span>
                          <span style={{ color: '#e2e8f0', fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2 }}>{ndR * neR}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.76rem', display: 'block', marginTop: 2 }}>
                            {interpretarProbabilidad(ndR * neR)}
                          </span>
                        </div>
                      )}
                      {liveCalcResidual && (
                        <>
                          <div style={{ minWidth: 110 }}>
                            <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block' }}>NR = NP × NC</span>
                            <span style={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2, color: RIESGO_COLORS[liveCalcResidual.nivel_riesgo_categoria] }}>
                              {liveCalcResidual.nivel_riesgo}
                            </span>
                            <span style={{
                              background: `${RIESGO_COLORS[liveCalcResidual.nivel_riesgo_categoria]}20`,
                              color: RIESGO_COLORS[liveCalcResidual.nivel_riesgo_categoria],
                              border: `1px solid ${RIESGO_COLORS[liveCalcResidual.nivel_riesgo_categoria]}50`,
                              borderRadius: 4, padding: '1px 8px', fontSize: '0.78rem', fontWeight: 800, display: 'inline-block', marginTop: 4
                            }}>
                              Nivel {liveCalcResidual.nivel_riesgo_categoria}
                            </span>
                          </div>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>Interpretación</span>
                            <span style={{ color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.5 }}>
                              {liveCalcResidual.interpretacion_riesgo}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginBottom: 4 }}>Aceptabilidad</span>
                            <span style={{
                              color: liveCalcResidual.aceptabilidad === 'Aceptable' ? RIESGO_COLORS['IV']
                                   : liveCalcResidual.aceptabilidad === 'Mejorable' ? RIESGO_COLORS['III']
                                   : RIESGO_COLORS['I'],
                              fontSize: '0.88rem', fontWeight: 700
                            }}>
                              {liveCalcResidual.aceptabilidad}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sección 8 */}
              <SectionDivider title="8. Metadatos de la evaluación" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
                <Field label="Evaluado por *">
                  <input style={inp} value={form.evaluado_por}
                    onChange={e => setForm(f => ({ ...f, evaluado_por: e.target.value }))}
                    placeholder="Nombre del evaluador" />
                </Field>
                <Field label="Fecha de evaluación *">
                  <input style={inp} type="date" value={form.fecha_evaluacion}
                    onChange={e => setForm(f => ({ ...f, fecha_evaluacion: e.target.value }))} />
                </Field>
              </div>
            </div>

            <div style={modalFooter}>
              <button onClick={() => !saving && setShowModal(false)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleSave} disabled={saving || !canEdit}
                style={{ ...saveBtn, opacity: (saving || !canEdit) ? 0.7 : 1 }}>
                {saving ? 'Guardando…' : (editTarget ? 'Guardar cambios' : 'Crear evaluación')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal eliminar ───────────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalBox, maxWidth: 420 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Eliminar evaluación</h2>
              <button onClick={() => !deleting && setDeleteTarget(null)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {deleteStep === 1 ? (
                <>
                  <p style={{ color: '#cbd5e1', fontSize: '0.88rem', margin: '0 0 8px' }}>
                    ¿Eliminar la evaluación del proceso{' '}
                    <strong style={{ color: '#f1f5f9' }}>{deleteTarget.process_name}</strong>?
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '0 0 20px' }}>
                    Esta acción no se puede deshacer.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteTarget(null)} style={cancelBtn}>Cancelar</button>
                    <button onClick={() => setDeleteStep(2)} style={{ ...saveBtn, background: '#dc2626' }}>Continuar</button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: '#fca5a5', fontSize: '0.88rem', fontWeight: 600, margin: '0 0 20px' }}>
                    ⚠ Confirma que deseas eliminar definitivamente esta evaluación. Esta acción no se puede deshacer.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setDeleteStep(1)} style={cancelBtn}>Atrás</button>
                    <button onClick={handleDelete} disabled={deleting}
                      style={{ ...saveBtn, background: '#dc2626', opacity: deleting ? 0.7 : 1 }}>
                      {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ background: '#1e293b', border: `1px solid ${color}30`, borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color, fontSize: '1.7rem', fontWeight: 800, lineHeight: 1 }}>{value}</span>
      <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function SectionDivider({ title }: { title: string }) {
  return (
    <div style={{ padding: '7px 12px', background: 'rgba(30,41,59,0.8)', border: '1px solid #2d3f52', borderRadius: 6, marginTop: 20 }}>
      <span style={{ color: '#60a5fa', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {title}
      </span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

function HelpText({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <p style={{ display: 'flex', gap: 6, alignItems: 'flex-start', color: '#64748b', fontSize: '0.72rem', lineHeight: 1.4, margin: '4px 0 0' }}>
      <span style={{ flexShrink: 0 }}>ℹ️</span>
      <span>{text}</span>
    </p>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: checked ? '#2FBF71' : '#334155', transition: 'background 0.2s', position: 'relative', flexShrink: 0 }}
    >
      <span style={{ position: 'absolute', top: 3, left: checked ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', pointerEvents: 'none' }} />
    </button>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn:  React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const thStyle:     React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:          React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const overlay:     React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox:    React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
const ta:          React.CSSProperties = { ...inp, resize: 'vertical', minHeight: 62 };
const closeBtn:    React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn:   React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn:     React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const suggestedBoxStyle:         React.CSSProperties = { background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: '12px 14px' };
const suggestedLabelStyle:       React.CSSProperties = { color: '#a5b4fc', fontSize: '0.76rem', fontWeight: 700, margin: '0 0 8px' };
const suggestedChipStyle:        React.CSSProperties = { padding: '5px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 999, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.15s' };
const suggestedChipActiveStyle:  React.CSSProperties = { background: 'rgba(47,191,113,0.15)', border: '1px solid #2FBF71', color: '#2FBF71' };
